/* globals EasyPost */
import Backbone from 'backbone';

import { setLocation, getQueryParams } from 'utils/wrappers';

import Base from 'app/routers/base';

import AccountNavItem from 'app/models/account_nav_item';

import AccountNavView from 'app/views/account_nav_view';
import UserNavView from 'app/views/user_nav_view';

/* Pages */
import ApiKeysPage from 'app/components/pages/api_keys';
import BatchesPage from 'app/components/pages/batches';
import BillingPage from 'app/components/pages/billing';
import BrandPage from 'app/components/pages/brand';
import CarrierAccountsPage from 'app/components/pages/carrier_accounts';
import CarrierActivityMapPage from 'app/components/pages/carrier_activity_map';
import DelayedTrackerPage from 'app/components/pages/delayed_trackers';
import FulfillmentProductsPage from 'app/components/pages/fulfillment_products';
import FulfillmentOrdersPage from 'app/components/pages/fulfillment_orders';
import FulfillmentBOLsPage from 'app/components/pages/fulfillment_bols';
import FulfillmentMarketplaceAccounts from 'app/components/pages/fulfillment_marketplace_accounts';
import InsurancesPage from 'app/components/pages/insurances';
import ReportDetailPage from 'app/components/pages/report_detail';
import ReportsPage from 'app/components/pages/reports';
import ShipmentsPage from 'app/components/pages/shipments';
import TrackersPage from 'app/components/pages/trackers';
import TrackingStatusReportPage from 'app/components/pages/tracking_status_report';
import UserEditPage from 'app/components/pages/user_edit';
import WebhooksAndEventsPage from 'app/components/pages/webhooks_and_events';

export const PAGE_SELECTOR = '.account';
export const ACL_ANALYTICS = 'analytics';
export const ACL_FULFILLMENT = 'fulfillment';
export const ACL_SHIPMENT_CREATION = 'shipments_creation';
export const ACL_THIRDPARTY_FULFILLMENT = 'thirdparty_fulfillment';

export default Base.extend({
  targetEl: '#account-page',

  routes: {
    account: 'dashboard',
    'account/api-keys': 'apiKeys',
    'account/batches': 'batches',
    'account/billing': 'billing',
    'account/brand': 'brand',
    'account/carriers': 'carrierAccounts',
    'account/carrier-activity-map': 'carrierActivityMap',
    'account/delayed-trackers': 'delayedTrackers',
    'account/fulfillment-products': 'fulfillmentProducts',
    'account/fulfillment-orders': 'fulfillmentOrders',
    'account/fulfillment-bols': 'fulfillmentBOLs',
    'account/fulfillment-marketplace-accounts': 'fulfillmentMarketplaceAccounts',
    'account/insurances': 'insurances',
    'account/reports/:reportType': 'reports',
    'account/shipments': 'shipments',
    'account/tracking-status-report': 'trackingStatusReport',
    'account/trackers': 'trackers',
    'account/user': 'user',
    'account/webhooks-and-events': 'webhooksAndEvents',
  },

  initialize() {
    this.currentUser = EasyPost.Globals.currentUser;
    const userNavCollection = new Backbone.Collection([
      new AccountNavItem({ route: 'api-keys', readable: 'API Keys' }),
      new AccountNavItem({ route: 'brand', readable: 'Brand' }),
      new AccountNavItem({ route: 'billing', readable: 'Billing' }),
      new AccountNavItem({ route: 'carriers', readable: 'Carrier Accounts' }),
      new AccountNavItem({ route: 'user', readable: 'User Info' }),
    ]);

    const accountNavCollection = new Backbone.Collection([
      new AccountNavItem({ route: 'shipments', readable: 'Shipments' }),
      new AccountNavItem({ route: 'trackers', readable: 'Trackers' }),
      new AccountNavItem({ route: 'insurances', readable: 'Insurance' }),
      new AccountNavItem({ route: 'batches', readable: 'Batches' }),
      new AccountNavItem({ route: 'reports/shipment', readable: 'Reports' }),
      new AccountNavItem({ route: 'webhooks-and-events', readable: 'Webhooks & Events' }),

      new AccountNavItem({ route: '', readable: 'Analytics', aclField: ACL_ANALYTICS }),
      new AccountNavItem({ route: 'delayed-trackers', readable: 'Delayed Trackers', aclField: ACL_ANALYTICS }),
      new AccountNavItem({ route: 'tracking-status-report', readable: 'Tracking Status Report', aclField: ACL_ANALYTICS }),
      new AccountNavItem({ route: 'carrier-activity-map', readable: 'Carrier Activity Map', aclField: ACL_ANALYTICS }),

      new AccountNavItem({ route: '', readable: 'Fulfillment', aclField: ACL_FULFILLMENT }),
      new AccountNavItem({ route: 'fulfillment-products', readable: 'Inventory', aclField: ACL_FULFILLMENT }),
      new AccountNavItem({ route: 'fulfillment-orders', readable: 'Orders', aclField: ACL_FULFILLMENT }),
      new AccountNavItem({ route: 'fulfillment-bols', readable: 'BOLs', aclField: ACL_FULFILLMENT }),
      new AccountNavItem({ route: 'fulfillment-marketplace-accounts', readable: 'Marketplace Accounts', aclField: ACL_THIRDPARTY_FULFILLMENT }),
    ]);

    this.userNavView = new UserNavView({ el: '#user-nav', collection: userNavCollection, model: this.currentUser });
    this.accountNavView = new AccountNavView({ el: '#account-nav', collection: accountNavCollection, model: this.currentUser });
  },

  renderAccountPage(ViewClass, options) {
    this.enforceLogin({
      callback: () => {
        const userACL = this.currentUser.get('acl') || {};
        if (options.aclField && !userACL[options.aclField]) {
          this.redirectToLoggedIn();
          return;
        }
        const view = new ViewClass({ ...options, query: getQueryParams() });
        const pageName = Backbone.history.getFragment().replace('account/', '');

        this.accountNavView.setPageAndRender(pageName);
        this.userNavView.setPageAndRender(pageName);
        this.renderView(view);
      },
    });
  },

  dashboard() {
    // check for any hash routes first
    const hash = this.checkForHash(2);
    if (hash.length) {
      setLocation(`account/${hash}`);
    } else {
      this.redirectToLoggedIn();
    }
  },

  apiKeys() {
    this.renderAccountPage(ApiKeysPage, {});
  },

  batches() {
    this.renderAccountPage(BatchesPage, {});
  },

  billing() {
    this.renderAccountPage(BillingPage, { user: this.currentUser });
  },

  brand() {
    this.renderAccountPage(BrandPage, { user: this.currentUser });
  },

  carrierAccounts() {
    this.renderAccountPage(CarrierAccountsPage, {});
  },

  carrierActivityMap() {
    this.renderAccountPage(CarrierActivityMapPage, { aclField: ACL_ANALYTICS });
  },

  delayedTrackers() {
    this.renderAccountPage(DelayedTrackerPage, { aclField: ACL_ANALYTICS });
  },

  fulfillmentOrders() {
    this.renderAccountPage(FulfillmentOrdersPage, { aclField: ACL_FULFILLMENT });
  },

  fulfillmentProducts() {
    this.renderAccountPage(FulfillmentProductsPage, { aclField: ACL_FULFILLMENT });
  },

  fulfillmentBOLs() {
    this.renderAccountPage(FulfillmentBOLsPage, { aclField: ACL_FULFILLMENT });
  },

  fulfillmentMarketplaceAccounts() {
    this.renderAccountPage(FulfillmentMarketplaceAccounts, {
      aclField: ACL_THIRDPARTY_FULFILLMENT,
      user: this.currentUser,
    });
  },

  insurances() {
    this.renderAccountPage(InsurancesPage, {});
  },

  reports(reportType) {
    // we can't have dynamic pages with our static site, so must look for the location hash
    let hash = this.checkForHash(2);
    if (hash.length) {
      hash = hash.replace('mode=', '').split('?');
      // if this url changes, needs to change as well
      this.renderAccountPage(ReportDetailPage, { reportType, reportId: hash[0], mode: hash[1] });
    } else {
      this.renderAccountPage(ReportsPage, { user: this.currentUser, reportType });
    }
  },

  shipments() {
    this.renderAccountPage(ShipmentsPage, { user: this.currentUser });
  },

  trackers() {
    this.renderAccountPage(TrackersPage, {});
  },

  trackingStatusReport() {
    this.renderAccountPage(TrackingStatusReportPage, { aclField: ACL_ANALYTICS });
  },

  user() {
    this.renderAccountPage(UserEditPage, { model: this.currentUser });
  },

  webhooksAndEvents() {
    this.renderAccountPage(WebhooksAndEventsPage, {});
  },
});



// WEBPACK FOOTER //
// ./app/routers/account_router.js